<template>
  <div>
    <v-btn outlined color="pink" to="/elections">
      <v-icon left>mdi-arrow-left-circle</v-icon>
      Return to elections list
    </v-btn>
    <section v-if="found" class="mb-5">
      <h1 class="mt-5 mb-3 display-2">
        {{ electionFound.association.description }}
        &mdash;
        <strong class="text-uppercase" v-text="electionFound.status"></strong>
      </h1>
      <h2>
        Using registration for
        <u>
          <strong>{{ electionFound.session.sSesName }}</strong>
        </u>
      </h2>
      <p class="overline d-flex grey--text">
        Created: {{ formatDate(electionFound.created_at) }}
      </p>
      <p>
        Time: {{ String(electionFound.start).substr(0, 5) }} &mdash;
        {{ String(electionFound.end).substr(0, 5) }} on
        {{ formatDateOnly(electionFound.date) }}
      </p>
    </section>

    <section :class="$vuetify.breakpoint.mdAndUp ? 'd-flex' : ''">
      <v-btn color="blue" class="mr-1 mb-1" :to="`/elections/${election}/eligible`">
        Eligible Voters
      </v-btn>
      <v-btn class="mr-1 mb-1" color="orange" @click="addStudentToVoters">
        Single Voter
      </v-btn>
      <v-menu offset-y v-if="electionFound.deletable || electionFound.status !== 'ended'">
        <template v-slot:activator="{ on, attrs }">
          <v-btn title="More Options" color="primary" dark v-bind="attrs" v-on="on">
            <v-icon>mdi-dots-horizontal</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item>
            <v-list-item-title v-if="electionFound.status !== 'ended'" @click="updatingTime = !updatingTime">Update
              Time</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="electionFound.deletable" @click="deleteElection">
            <v-list-item-title>Delete Election</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-spacer v-if="$vuetify.breakpoint.mdAndUp"></v-spacer>

      <v-btn color="purple" text class="mr-1 mb-1 white--text" @click="downloadElectionReport"
        :loading="downloadingResults" :disabled="downloadingResults" v-if="electionFound.status == 'ended'">
        Election Result
      </v-btn>
      <v-btn color="purple" text class="mr-1 mb-1 white--text" @click="downloadComprehensiveReport"
        :loading="downloadingComprehensive" :disabled="downloadingComprehensive" v-if="electionFound.status == 'ended'">
        Comprehensive Report
      </v-btn>
      <v-btn class="white--text" @click="changeElectionStatus" :disabled="cannotStartElection"
        :color="electionFound.status == 'live' ? 'red' : 'green'">
        {{ electionFound.status == "live" ? "End" : electionFound.status == "ended" ? "Restart" : "Start" }}
        Election
      </v-btn>
    </section>

    <section v-if="updatingTime">
      <UpdateElectionTime :start="electionFound.start" :end="electionFound.end" :election="election"
        @cancelled="updatingTime = false" @updated="loadElectionDetails" />
    </section>

    <v-divider class="my-7"></v-divider>

    <section class="mt-7">
      <h1 class="display-1 mb-2">
        {{ electionFound.voters }} accredited
        {{ electionFound.voters > 1 ? "voters" : "voter" }}
      </h1>
      <v-btn outlined color="blue" :to="`/elections/${election}/voters`">
        List of voters
      </v-btn>
    </section>

    <v-divider class="my-7"></v-divider>

    <section class="mt-7">
      <h1 class="display-1 mb-2">Positions</h1>
      <v-btn outlined color="teal" @click="addPosition"> Add position </v-btn>

      <v-expansion-panels class="mt-4 bg--teal" focusable multiple>
        <v-expansion-panel v-for="(position, i) in positions" :key="position.id">
          <v-expansion-panel-header class="white--text overline"
            :color="i % 2 == 0 ? 'teal darken-3' : 'blue-grey darken-3'">
            <strong>
              {{ position.description }} ({{ position.candidates.length }})
            </strong>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <section class="py-3">
              Restrictions:
              <span v-if="
                position.programme ||
                position.faculty ||
                position.department ||
                position.level
              ">
                <v-chip small outlined class="mx-1" v-if="position.programme">
                  Programme: {{ position.programme.sprogName }}
                </v-chip>
                <v-chip small outlined class="mx-1" v-if="position.faculty">
                  Faculty: {{ position.faculty.FacName }}
                </v-chip>
                <v-chip small outlined class="mx-1" v-if="position.department">
                  Department: {{ position.department.sdeptName }}
                </v-chip>
                <v-chip small outlined class="mx-1" v-if="position.level">
                  Level: {{ position.level }}00
                </v-chip>
              </span>
              <v-chip v-else small outlined color="green" class="white--text">
                Unrestricted
              </v-chip>
            </section>

            <p class="mt-3">
              <v-btn small class="mr-2" @click="addCandidateToPosition(position)">Add Candidate</v-btn>

              <v-btn depressed small dark color="red" @click="deletePosition(position.id)">
                Delete position
              </v-btn>
            </p>

            <v-list>
              <v-subheader>CANDIDATES</v-subheader>
              <v-list-item-group v-model="selected" color="secondary" v-if="position.candidates.length > 0">
                <v-list-item v-for="candidate in position.candidates" :key="candidate.id">
                  <v-list-item-icon>
                    <v-img cover top class="candidate-image" :src="`${candidate.profile.photo
                      ? `https://uniuyo.edu.ng/eportals/passports/${candidate.profile.photo}`
                      : '/favicon.png'
                      }`" transition="scale-transition" width="45" height="45">
                    </v-img>
                  </v-list-item-icon>

                  <v-list-item-content>
                    <v-list-item-title>
                      {{ candidate.profile.name }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ candidate.profile.reg_number }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-btn icon color="red" :disabled="removing" @click="removeCandidateFromPosition(candidate.id)">
                      <v-icon>mdi-close</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list-item-group>
              <h3 v-else class="my-2 mx-4 red--text">
                No candidate for this position
              </h3>
            </v-list>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </section>

    <section class="mt-15 mb-5">
      <v-btn color="grey" x-small plain :href="`${baseUrl}/print/${electionFound.id}/result`" target="_blank">Result</v-btn>
      <v-btn color="grey" x-small plain :href="`${baseUrl}/print/${electionFound.id}/comprehensive`" target="_blank">Comprehensive</v-btn>
    </section>

    <AddPosition />
    <AddCandidate />
    <AddStudentsToVoters />
  </div>
</template>

<script>
import AddPosition from "../../dialogs/AddPosition.vue";
import AddCandidate from "../../dialogs/AddCandidate.vue";
import AddStudentsToVoters from "../../dialogs/AddStudentToVoters.vue";
import UpdateElectionTime from "./includes/UpdateElectionTime.vue";
import { formatDate, formatDateOnly, formatTime } from "../../tools/format";
// import { flattenObject } from "../../tools/object";

export default {
  props: ["election"],

  components: { AddPosition, AddCandidate, AddStudentsToVoters, UpdateElectionTime },

  data: () => ({
    electionFound: {},
    positions: [],
    found: false,
    selected: null,
    removing: false, // candidate
    deleting: false, // position
    downloadingResults: false,
    downloadingComprehensive: false,
    updatingTime: false
  }),

  computed: {
    baseUrl() {
      return String(process.env.VUE_APP_API_URL).split('/api')[0];
    },
    cannotStartElection() {
      const doesNotHaveCandidatesForAllPositions =
        this.positions.find((position) => {
          return position.candidates.length == 0;
        }) instanceof Object;

      return (
        this.electionFound.voters == 0 ||
        this.positions.length == 0 ||
        doesNotHaveCandidatesForAllPositions
      );
    },
  },

  methods: {
    formatTime,
    formatDate,
    formatDateOnly,
    loadElectionDetails() {
      this.found = false;
      this.$axios
        .get(`/election/${this.election}`)
        .then(({ data: election }) => {
          const { association, session, positions } = election;
          this.association = association;
          this.positions = positions;
          this.session = session;

          this.electionFound = election;

          this.found = true;
        })
        .catch((error) => {
          if (error.response.status == 404)
            this.$router.push({ name: "elections" });

          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Election not found",
          });
        });
    },
    changeElectionStatus() {
      this.$axios({
        url: "/election/status",
        method: "PUT",
        params: {
          election: this.election,
        },
      })
        .then(({ data: { message: status } }) => {
          this.$bus.$emit("snackbar", {
            type: "warning",
            message: `Election is now ${status}`,
          });

          this.loadElectionDetails();
        })
        .catch(() => {
          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Could not change election status",
          });
        });
    },
    deleteElection() {
      if (
        confirm("Removing this election will remove all the voters, positions and candidates. Proceed?")
      ) {
        this.deleting = true;

        this.$axios({
          url: `/election/${this.election}`,
          method: "DELETE",
        })
          .then(() => {
            this.$router.push("elections");

            this.$bus.$emit("snackbar", {
              type: "success",
              message: "Election deleted",
            });
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not delete election",
            });
          })
          .finally(() => {
            this.deleting = false;
          });
      }
    },
    addPosition() {
      this.$bus.$emit("position.add", this.election);
    },
    deletePosition(position) {
      if (
        confirm("Removing this position will remove the candidates contesting")
      ) {
        this.deleting = true;

        this.$axios({
          url: "/position",
          method: "DELETE",
          params: {
            position: position,
            election: this.election,
          },
        })
          .then(() => {
            this.$bus.$emit("snackbar", {
              type: "success",
              message: "Position deleted",
            });

            this.loadElectionDetails();
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not delete position",
            });
          })
          .finally(() => {
            this.deleting = false;
          });
      }
    },
    addStudentToVoters() {
      this.$bus.$emit("voters.student.add", this.election);
    },
    addCandidateToPosition(position) {
      this.$bus.$emit("candidate.add", this.election, position);
    },
    removeCandidateFromPosition(candidate) {
      if (confirm("Are you sure you want to remove this candidate?")) {
        this.removing = true;

        this.$axios({
          url: "/candidate",
          method: "DELETE",
          params: {
            candidate: candidate,
            election: this.election,
          },
        })
          .then(() => {
            this.$bus.$emit("snackbar", {
              type: "success",
              message: "Candidate removed from contesting",
            });

            this.loadElectionDetails();
          })
          .catch(() => {
            this.$bus.$emit("snackbar", {
              type: "error",
              message: "Could not remove candidate",
            });
          })
          .finally(() => {
            this.removing = false;
          });
      }
    },
    downloadElectionReport() {
      const { id } = this.electionFound;

      this.downloadingResults = true;

      this.$axios({
        url: `report/${id}/result`,
        method: "GET",
        responseType: "blob",
      })
        .then((response) => {


          this.downloadBlobAsFile(
            response.data,
            `${this.electionFound.association.description} Election Results | Conducted on ${formatDateOnly(this.electionFound.date)}.pdf`
          );

          this.$bus.$emit("snackbar", {
            type: "success",
            message: "Election result downloaded successfully",
          });
        })
        .catch(() => {

          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Could not download election result",
          });
        })
        .finally(() => {

          this.downloadingResults = false;
        });
    },
    downloadComprehensiveReport() {
      const { id } = this.electionFound;

      this.downloadingComprehensive = true;

      this.$axios({
        url: `report/${id}/comprehensive`,
        method: "GET",
        responseType: "blob",
      })
        .then((response) => {

          this.downloadBlobAsFile(
            response.data,
            `${this.electionFound.association.description} Comprehensive Election Report | Conducted on ${formatDateOnly(this.electionFound.date)}.pdf`
          );

          this.$bus.$emit("snackbar", {
            type: "success",
            message: "Comprehensive report downloaded successfully",
          });
        })
        .catch(() => {

          this.$bus.$emit("snackbar", {
            type: "error",
            message: "Could not download comprehensive report",
          });
        })
        .finally(() => {
          this.downloadingComprehensive = false;
        });
    },
    downloadBlobAsFile(data, filename) {
      let fileURL = window.URL.createObjectURL(new Blob([data]));
      let fileLink = document.createElement("a");

      fileLink.href = fileURL;
      fileLink.setAttribute("download", filename);
      document.body.appendChild(fileLink);
      fileLink.click();
    }
  },

  mounted() {
    this.loadElectionDetails();
    this.$bus.$on("election.updated", this.loadElectionDetails);
  },
};
</script>

